/**
 * Inventory Valuation Report Component
 *
 * Displays inventory valuation using FIFO and weighted average methods
 *
 * @module pages/inventory/InventoryValuation
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { getInventoryValuation } from '../../services/inventoryService';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { INVENTORY_CATEGORY_LABELS } from '../../utils/constants';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';

const InventoryValuation = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [categoryFilter, setCategoryFilter] = useState('');

  // Fetch valuation data
  const { data, isLoading } = useQuery({
    queryKey: ['inventory-valuation', categoryFilter],
    queryFn: () => getInventoryValuation({ category: categoryFilter }),
  });

  const items = data?.data || [];
  const totals = useMemo(() => data?.totals || {}, [data]);

  // Calculate potential margin
  const potentialMargin = useMemo(() => {
    if (totals.totalPotentialRevenue && totals.totalFifoValue) {
      return totals.totalPotentialRevenue - totals.totalFifoValue;
    }
    return 0;
  }, [totals]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando valuacion...</span>
          </Spinner>
        </Container>
      </DashboardLayout>
    );
  }

  // admin-only: this page exposes purchase costs and margins
  if (!isAdmin) {
    return (
      <DashboardLayout>
        <Container className="py-5 text-center">
          <Alert variant="warning">
            <i className="bi bi-lock me-2"></i>
            No tienes permiso para ver este reporte.
          </Alert>
          <Button variant="outline-secondary" onClick={() => navigate('/inventario')}>
            Volver al Inventario
          </Button>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <h1 className="h3 mb-0">Valuación de Inventario</h1>
                <p className="text-muted mb-0">Reporte de costos y valor del inventario</p>
              </div>
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/inventario')}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Volver al Inventario
              </Button>
            </div>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row className="mb-4 g-3">
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 rounded p-3">
                      <i className="bi bi-box-seam fs-4 text-primary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Total Artículos</p>
                    <h3 className="mb-0">{formatNumber(totals.totalItems || 0)}</h3>
                    <small className="text-muted">{formatNumber(totals.totalStock || 0)} unidades</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-info bg-opacity-10 rounded p-3">
                      <i className="bi bi-stack fs-4 text-info"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Valor FIFO</p>
                    <h3 className="mb-0 fs-5">{formatCurrency(totals.totalFifoValue || 0)}</h3>
                    <small className="text-muted">{formatNumber(totals.totalBatches || 0)} lotes activos</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-success bg-opacity-10 rounded p-3">
                      <i className="bi bi-currency-dollar fs-4 text-success"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Ingreso Potencial</p>
                    <h3 className="mb-0 fs-5">{formatCurrency(totals.totalPotentialRevenue || 0)}</h3>
                    <small className="text-muted">a precio de lista</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-warning bg-opacity-10 rounded p-3">
                      <i className="bi bi-graph-up-arrow fs-4 text-warning"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Margen Potencial</p>
                    <h3 className="mb-0 fs-5">{formatCurrency(potentialMargin)}</h3>
                    <small className="text-muted">
                      {totals.totalPotentialRevenue > 0
                        ? `${((potentialMargin / totals.totalPotentialRevenue) * 100).toFixed(1)}%`
                        : '0%'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filter */}
        <Row className="mb-4">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small text-muted">Filtrar por Categoría</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Todas las categorías</option>
                {Object.entries(INVENTORY_CATEGORY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Valuation Table - Desktop */}
        <Row className="d-none d-md-flex">
          <Col>
            <Card className="shadow-sm">
              <Card.Body className="p-0">
                {items.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>SKU</th>
                          <th>Nombre</th>
                          <th>Categoria</th>
                          <th className="text-end">Stock</th>
                          <th className="text-center">Lotes</th>
                          <th className="text-end">Costo Prom.</th>
                          <th className="text-end">Valor FIFO</th>
                          <th className="text-end">Precio Lista</th>
                          <th className="text-end">Ingreso Potencial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <code className="text-primary">{item.sku || '-'}</code>
                            </td>
                            <td><strong>{item.name}</strong></td>
                            <td>
                              <small className="text-muted">
                                {INVENTORY_CATEGORY_LABELS[item.category] || item.category}
                              </small>
                            </td>
                            <td className="text-end">
                              {formatNumber(item.current_stock)} {item.unit}
                            </td>
                            <td className="text-center">{item.batch_count}</td>
                            <td className="text-end">{formatCurrency(item.weighted_avg_cost)}</td>
                            <td className="text-end">
                              <strong>{formatCurrency(item.fifo_value)}</strong>
                            </td>
                            <td className="text-end">
                              {item.sale_price ? formatCurrency(item.sale_price) : '-'}
                            </td>
                            <td className="text-end">
                              {item.potential_revenue
                                ? <span className="text-success">{formatCurrency(item.potential_revenue)}</span>
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light fw-bold">
                        <tr>
                          <td colSpan={3}>TOTALES</td>
                          <td className="text-end">{formatNumber(totals.totalStock)}</td>
                          <td className="text-center">{totals.totalBatches}</td>
                          <td className="text-end">-</td>
                          <td className="text-end">{formatCurrency(totals.totalFifoValue)}</td>
                          <td className="text-end">-</td>
                          <td className="text-end text-success">{formatCurrency(totals.totalPotentialRevenue)}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                ) : (
                  <div className="p-5 text-center text-muted">
                    <i className="bi bi-box-seam fs-1 d-block mb-3"></i>
                    <p className="mb-0">No hay artículos en el inventario</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Valuation Cards - Mobile */}
        <Row className="d-md-none g-3">
          {items.length > 0 ? (
            items.map((item) => (
              <Col xs={12} key={item.id}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">{item.name}</h6>
                        {item.sku && (
                          <code className="text-primary small">{item.sku}</code>
                        )}
                      </div>
                      <small className="text-muted">
                        {INVENTORY_CATEGORY_LABELS[item.category] || item.category}
                      </small>
                    </div>

                    <Row className="g-2">
                      <Col xs={6}>
                        <small className="text-muted d-block">Stock</small>
                        <strong>{formatNumber(item.current_stock)} {item.unit}</strong>
                      </Col>
                      <Col xs={6}>
                        <small className="text-muted d-block">Lotes</small>
                        <strong>{item.batch_count}</strong>
                      </Col>
                      <Col xs={6}>
                        <small className="text-muted d-block">Costo Prom.</small>
                        <strong>{formatCurrency(item.weighted_avg_cost)}</strong>
                      </Col>
                      <Col xs={6}>
                        <small className="text-muted d-block">Valor FIFO</small>
                        <strong className="text-primary">{formatCurrency(item.fifo_value)}</strong>
                      </Col>
                      <Col xs={6}>
                        <small className="text-muted d-block">Precio Lista</small>
                        <strong>{item.sale_price ? formatCurrency(item.sale_price) : '-'}</strong>
                      </Col>
                      <Col xs={6}>
                        <small className="text-muted d-block">Ingreso Potencial</small>
                        <strong className="text-success">
                          {item.potential_revenue ? formatCurrency(item.potential_revenue) : '-'}
                        </strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))
          ) : (
            <Col xs={12}>
              <Card className="shadow-sm">
                <Card.Body className="p-5 text-center text-muted">
                  <i className="bi bi-box-seam fs-1 d-block mb-3"></i>
                  <p className="mb-0">No hay artículos en el inventario</p>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </DashboardLayout>
  );
};

export default InventoryValuation;
